import { useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { listingsAPI } from '../api/client'
import { normalizeImeiDigits, sanitizeImeiFieldInput } from '../utils/extractImei'
import { publicAssetUrl } from '../utils/assetUrl'
import { hasListingPhotos } from '../utils/stockPhoneImage'
import { type InBoxCompleteness } from '../utils/inBoxCompleteness'
import { isBasicPhoneModel, type ConditionRating } from '@redevicer/shared'
import {
  batteryOriginalFromParts,
  compactPartsAuthenticity,
  EMPTY_PARTS_AUTHENTICITY,
  mergeBatteryIntoParts,
  normalizePartsAuthenticity,
  type PartsAuthenticityMap,
  resolvePhoneModelMinReleaseYear,
  clampListingReleaseYear,
  LISTING_RELEASE_YEAR_FLOOR,
} from '@redevicer/shared'
import BatteryWheelPicker from './BatteryWheelPicker'
import ConditionPickerGrid from './ConditionPickerGrid'
import InBoxPickerGrid from './InBoxPickerGrid'
import UsageDurationPickerGrid from './UsageDurationPickerGrid'
import SelectionPickerGrid from './SelectionPickerGrid'
import DefectMarkPad, { type DefectMark } from './DefectMarkPad'
import PartsAuthenticityFields from './PartsAuthenticityFields'
import SellFormPanel from './SellFormPanel'
import WarrantyFields from './WarrantyFields'
import YearWheelPicker from './YearWheelPicker'

export type EditableListing = {
  id: number
  title: string
  description?: string | null
  price: number
  condition: string
  battery_health?: number | null
  battery_cycles?: number | null
  battery_original?: boolean | null
  battery_tested_at?: string | null
  imei?: string | null
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  usage_duration?: string | null
  is_smartphone?: boolean
  shipment_method?: string
  in_box_completeness?: string
  release_year?: number | null
  defect_marks?: DefectMark[] | null
  parts_authenticity?: PartsAuthenticityMap | null
  phone_model: { manufacturer: string; model_name: string; release_year?: number | null }
  images?: Array<{
    id: number
    thumbnail_url: string
    display_url?: string
    is_primary: boolean
  }>
}

const CURRENT_YEAR = new Date().getFullYear()

const SHIPMENT_OPTIONS = [
  ['courier', 'listing.shipmentCourier', 'listing.shipmentCourierHint'],
  ['locker', 'listing.shipmentLocker', 'listing.shipmentLockerHint'],
  ['handover', 'listing.shipmentHandover', 'listing.shipmentHandoverHint'],
] as const

function dateInputValue(v: string | null | undefined): string {
  if (!v) return ''
  return String(v).slice(0, 10)
}

function coverPhotoUrl(listing: EditableListing): string | null {
  if (!hasListingPhotos(listing.images)) return null
  const primary = listing.images!.find((img) => img.is_primary) ?? listing.images![0]
  return publicAssetUrl(primary.display_url || primary.thumbnail_url || '')
}

export default function ListingEditForm({
  listing,
  token,
  busy,
  onBusy,
  onCancel,
  onSaved,
  onError,
}: {
  listing: EditableListing
  token: string
  busy: boolean
  onBusy: (busy: boolean) => void
  onCancel: () => void
  onSaved: () => void
  onError: (message: string) => void
}) {
  const { t } = useTranslation()
  const shipmentOptions = useMemo(
    () =>
      SHIPMENT_OPTIONS.map(([key, labelKey, hintKey]) => ({
        value: key,
        title: t(labelKey),
        hint: t(hintKey),
      })),
    [t],
  )
  const showBattery = listing.is_smartphone !== false && !isBasicPhoneModel(listing.phone_model)

  const [title, setTitle] = useState(listing.title)
  const [description, setDescription] = useState(listing.description?.trim() ?? '')
  const [price, setPrice] = useState(String(listing.price))
  const [condition, setCondition] = useState<ConditionRating | ''>(
    listing.condition as ConditionRating,
  )
  const [batteryHealth, setBatteryHealth] = useState(listing.battery_health ?? 100)
  const [batteryCycles, setBatteryCycles] = useState(
    listing.battery_cycles != null ? String(listing.battery_cycles) : '',
  )
  const [batteryOriginal, setBatteryOriginal] = useState(listing.battery_original !== false)
  const [batteryTestedAt, setBatteryTestedAt] = useState(
    dateInputValue(listing.battery_tested_at) || '',
  )
  const modelMinReleaseYear = useMemo(
    () =>
      resolvePhoneModelMinReleaseYear(
        listing.phone_model.manufacturer,
        listing.phone_model.model_name,
        listing.phone_model.release_year,
      ),
    [listing.phone_model],
  )

  const [releaseYearUnknown, setReleaseYearUnknown] = useState(listing.release_year == null)
  const [releaseYear, setReleaseYear] = useState<number>(() =>
    clampListingReleaseYear(
      listing.release_year ?? listing.phone_model.release_year ?? CURRENT_YEAR,
      modelMinReleaseYear,
    ),
  )
  const [imei, setImei] = useState((listing.imei || '').replace(/\D/g, '').slice(0, 15))
  const [usageDuration, setUsageDuration] = useState(listing.usage_duration || '')
  const [hasFactoryWarranty, setHasFactoryWarranty] = useState(!!listing.has_factory_warranty)
  const [warrantyRemaining, setWarrantyRemaining] = useState(listing.warranty_remaining || '')
  const [inBoxCompleteness, setInBoxCompleteness] = useState<InBoxCompleteness>(
    (listing.in_box_completeness as InBoxCompleteness) || 'phone_only',
  )
  const [shipmentMethod, setShipmentMethod] = useState<'courier' | 'locker' | 'handover'>(
    listing.shipment_method === 'locker' || listing.shipment_method === 'handover'
      ? listing.shipment_method
      : 'courier',
  )
  const [defectMarks, setDefectMarks] = useState<DefectMark[]>(listing.defect_marks ?? [])
  const [partsAuth, setPartsAuth] = useState<PartsAuthenticityMap>(() =>
    mergeBatteryIntoParts(
      { ...EMPTY_PARTS_AUTHENTICITY, ...normalizePartsAuthenticity(listing.parts_authenticity) },
      listing.battery_original,
    ),
  )

  const coverUrl = useMemo(() => coverPhotoUrl(listing), [listing])

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const priceNum = Number(price.replace(',', '.'))
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      onError(t('myListings.invalidPrice', 'Enter a valid price.'))
      return
    }
    if (!condition) {
      onError(t('listing.conditionRequired', 'Pasirinkite būklę.'))
      return
    }
    const imeiDigits = normalizeImeiDigits(imei)
    if (imeiDigits.length > 0 && imeiDigits.length !== 15) {
      onError(t('listing.imeiNeed15Digits'))
      return
    }

    const cyclesNum = parseInt(batteryCycles, 10)
    const payload: Record<string, unknown> = {
      title: title.trim() || listing.title,
      description: description.trim() || null,
      price: priceNum,
      condition,
      shipment_method: shipmentMethod,
      in_box_completeness: inBoxCompleteness,
      usage_duration: usageDuration || null,
      has_factory_warranty: hasFactoryWarranty,
      warranty_remaining: hasFactoryWarranty ? warrantyRemaining || null : null,
      release_year: releaseYearUnknown ? null : releaseYear,
      imei: imeiDigits.length === 15 ? imeiDigits : null,
      defect_marks: defectMarks,
      parts_authenticity: showBattery ? compactPartsAuthenticity(partsAuth) : undefined,
    }
    if (showBattery) {
      payload.battery_health = batteryHealth
      payload.battery_cycles = Number.isFinite(cyclesNum) && cyclesNum >= 0 ? cyclesNum : null
      payload.battery_original = batteryOriginalFromParts(partsAuth) ?? batteryOriginal
      payload.battery_tested_at = batteryTestedAt || null
    }

    onBusy(true)
    listingsAPI
      .updateListing(listing.id, payload, token)
      .then(() => onSaved())
      .catch((err) => onError(err.response?.data?.detail || t('myListings.updateFailed', 'Update failed')))
      .finally(() => onBusy(false))
  }

  return (
    <form
      className="listing-form listing-form--modern seller-listing-edit-form"
      onSubmit={handleSubmit}
    >
      <p className="seller-listing-edit-form__model">
        {listing.phone_model.manufacturer} {listing.phone_model.model_name}
      </p>
      <p className="seller-listing-edit-form__lead">{t('myListings.editLead')}</p>

      <div className="seller-listing-edit__fields">
        <label className="seller-listing-edit__field">
          <span className="seller-listing-edit__label">{t('listing.form.titleField')}</span>
          <input
            className="seller-listing-edit__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="seller-listing-edit__field seller-listing-edit__field--price">
          <span className="seller-listing-edit__label">{t('listing.form.price')}</span>
          <input
            className="seller-listing-edit__input"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
          />
        </label>
      </div>

      <label className="seller-listing-edit__field">
        <span className="seller-listing-edit__label">{t('listing.form.description')}</span>
        <textarea
          className="seller-listing-edit__input seller-listing-edit__textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className="listing-field listing-condition-field listing-condition-field--option-caps">
        <span className="listing-field__label">{t('listing.conditionLabel')}</span>
        <ConditionPickerGrid
          value={condition}
          onChange={(v) => setCondition(v)}
        />
      </div>

      <div className="seller-listing-edit-spec">
        <div className="seller-listing-edit-spec__cell">
          <span className="seller-listing-edit__label">{t('listing.releaseYear')}</span>
          <YearWheelPicker
            value={releaseYear}
            onChange={(y) => {
              setReleaseYearUnknown(false)
              setReleaseYear(y)
            }}
            min={modelMinReleaseYear}
            unknown={releaseYearUnknown}
            onUnknown={() => setReleaseYearUnknown(true)}
            unknownLabel={t('listing.releaseYearUnknown', 'Nežinoma')}
          />
        </div>
        {showBattery ? (
          <div className="seller-listing-edit-spec__cell">
            <span className="seller-listing-edit__label">{t('listing.form.batteryLabel')}</span>
            <BatteryWheelPicker value={batteryHealth} onChange={setBatteryHealth} compact />
          </div>
        ) : null}
      </div>

      {showBattery ? (
        <>
        <div className="battery-transparency-form">
          <p className="battery-transparency-form__title">{t('batteryTransparency.title')}</p>
          <label className="battery-transparency-form__field">
            <span>{t('batteryTransparency.form.cycles')}</span>
            <input
              type="number"
              min={0}
              max={10000}
              inputMode="numeric"
              value={batteryCycles}
              onChange={(e) => setBatteryCycles(e.target.value)}
              placeholder="347"
            />
          </label>
          <p className="listing-spec-row-hint">{t('batteryTransparency.form.cyclesHint')}</p>
          <div className="battery-transparency-form__origin" role="group">
            <button
              type="button"
              className={`battery-transparency-form__chip${batteryOriginal ? ' battery-transparency-form__chip--on' : ''}`}
              onClick={() => setBatteryOriginal(true)}
            >
              {t('batteryTransparency.form.original')}
            </button>
            <button
              type="button"
              className={`battery-transparency-form__chip${!batteryOriginal ? ' battery-transparency-form__chip--on' : ''}`}
              onClick={() => setBatteryOriginal(false)}
            >
              {t('batteryTransparency.form.replaced')}
            </button>
          </div>
          <label className="battery-transparency-form__field">
            <span>{t('batteryTransparency.form.tested')}</span>
            <input
              type="date"
              value={batteryTestedAt}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setBatteryTestedAt(e.target.value)}
            />
          </label>
        </div>
        <PartsAuthenticityFields
          value={partsAuth}
          onChange={setPartsAuth}
          manufacturer={listing.phone_model.manufacturer}
          modelName={listing.phone_model.model_name}
        />
        </>
      ) : null}

      <label className="seller-listing-edit__field">
        <span className="seller-listing-edit__label">{t('listing.imeiLabel')}</span>
        <input
          className="seller-listing-edit__input"
          inputMode="numeric"
          autoComplete="off"
          value={imei}
          onChange={(e) => setImei(sanitizeImeiFieldInput(e.target.value))}
          placeholder="35..."
        />
        <span className="seller-listing-edit-form__hint">{t('myListings.imeiChangeNote')}</span>
      </label>

      <div className="listing-field listing-condition-field">
        <span className="listing-field__label">{t('listing.usageLegend')}</span>
        <p className="listing-condition-lead">{t('listing.usageIntro')}</p>
        <UsageDurationPickerGrid value={usageDuration} onChange={setUsageDuration} />
      </div>

      <WarrantyFields
        hasWarranty={hasFactoryWarranty}
        warrantyRemaining={warrantyRemaining}
        onHasWarrantyChange={setHasFactoryWarranty}
        onWarrantyRemainingChange={setWarrantyRemaining}
        panelIndex={2}
      />

      <div className="listing-field listing-condition-field listing-condition-field--option-caps">
        <span className="listing-field__label">{t('listing.kitLegend')}</span>
        <p className="listing-condition-lead">{t('listing.kitIntro')}</p>
        <InBoxPickerGrid value={inBoxCompleteness} onChange={setInBoxCompleteness} />
      </div>

      <SellFormPanel
        title={t('listing.defectMarkTitle')}
        lead={t('listing.defectMarkHint')}
        index={4}
        className="sell-form-panel--defects"
      >
        <DefectMarkPad embedded imageUrl={coverUrl} marks={defectMarks} onChange={setDefectMarks} />
      </SellFormPanel>

      <div className="listing-field listing-condition-field">
        <span className="listing-field__label">{t('listing.shipmentLegend')}</span>
        <p className="listing-condition-lead">{t('listing.shipmentIntro')}</p>
        <SelectionPickerGrid
          options={shipmentOptions}
          value={shipmentMethod}
          onChange={(next) => setShipmentMethod(next as 'courier' | 'locker' | 'handover')}
          ariaLabel={t('listing.shipmentLegend')}
          stack
        />
      </div>

      <div className="seller-listing-edit-actions seller-listing-edit-actions--modern seller-listing-edit-actions--pair">
        <button type="submit" className="seller-listing-btn seller-listing-btn--primary" disabled={busy}>
          {t('common.save')}
        </button>
        <button
          type="button"
          className="seller-listing-btn seller-listing-btn--ghost"
          onClick={onCancel}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}
